import { createStart, createCsrfMiddleware, createMiddleware } from "@tanstack/react-start";

import { attachSupabaseAuth } from "./lib/attach-supabase-auth";
import { renderErrorPage } from "./lib/error-page";
import { findRedirect } from "./lib/redirects.server";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

/**
 * Barra no final, sempre. As URLs do site foram planejadas com barra final e
 * é assim que o sitemap e o canonical as declaram. Sem isto, o roteador
 * responderia 307 (temporário) em /blog/ ou em /blog, e cada URL do sitemap
 * custaria um salto de rastreamento ao Google. O 301 é permanente e consolida
 * o sinal numa única URL. A raiz "/" fica como está, e caminhos de arquivo,
 * API e funções de servidor ficam de fora.
 */
const trailingSlashMiddleware = createMiddleware({ type: "request" }).server(async ({ request, next }) => {
  const url = new URL(request.url);
  const { pathname } = url;
  const isento =
    pathname === "/" ||
    pathname.endsWith("/") ||
    pathname.includes(".") ||
    pathname.startsWith("/api/") ||
    pathname.startsWith("/lovable/") ||
    pathname.startsWith("/_serverFn");
  if (!isento) {
    url.pathname = `${pathname}/`;
    return new Response(null, { status: 301, headers: { location: url.toString() } });
  }
  return next();
});

const redirectsMiddleware = createMiddleware({ type: "request" }).server(async ({ request, next }) => {
  if (request.method !== "GET" && request.method !== "HEAD") return next();
  const url = new URL(request.url);
  const { pathname } = url;
  // Arquivos antigos do WordPress (ex.: PDFs em /wp-content/) também podem
  // ser redirecionados; só os arquivos do próprio site ficam de fora.
  const isSystemPath =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/api/") ||
    pathname.startsWith("/lovable/") ||
    pathname.startsWith("/_serverFn") ||
    /^\/(assets|fonts|img|imagens|icones)\//.test(pathname) ||
    /\.(js|css|map|woff2?|webp|png|jpe?g|svg|ico|txt|xml)$/i.test(pathname);
  if (isSystemPath) return next();

  try {
    const record = await findRedirect(pathname);
    if (!record) return next();
    const destination = new URL(record.destination_path, url.origin);
    destination.search = url.search;
    return new Response(null, {
      status: record.status_code === 302 ? 302 : 301,
      headers: { location: destination.toString() },
    });
  } catch (error) {
    console.error("Não foi possível consultar redirecionamentos", error);
    return next();
  }
});

// Start installs this automatically when src/start.ts is absent; defining the
// file opts out, so re-add it explicitly to keep server functions protected
// from cross-site requests.
/**
 * Cabeçalhos de cache. Páginas públicas ficam 5 minutos na borda e são
 * servidas guardadas enquanto atualizam. Painel e qualquer resposta com
 * sessão nunca são guardados. Arquivos estáticos que passarem pelo servidor
 * recebem o mesmo cabeçalho do public/_headers.
 */
const cacheMiddleware = createMiddleware({ type: "request" }).server(async ({ request, next }) => {
  const result = await next();
  const response = (result as { response?: Response }).response ?? (result as unknown as Response);
  if (!(response instanceof Response)) return result;
  const { pathname } = new URL(request.url);
  const cookie = request.headers.get("cookie") ?? "";
  const autenticado = request.headers.has("authorization") || /sb-[^=]*-auth-token/.test(cookie);
  let valor: string | null = null;
  if (pathname.startsWith("/fonts/")) valor = "public, max-age=31536000, immutable";
  else if (/^\/(img|imagens|icones)\//.test(pathname)) valor = "public, max-age=2592000";
  else if (pathname.startsWith("/admin") || autenticado) valor = "private, no-store";
  else if (
    (request.method === "GET" || request.method === "HEAD") &&
    response.status === 200 &&
    (response.headers.get("content-type") ?? "").includes("text/html")
  ) valor = "public, max-age=0, s-maxage=300, stale-while-revalidate=86400";
  if (!valor) return result;
  try {
    response.headers.set("cache-control", valor);
    return result;
  } catch {
    const copia = new Response(response.body, response);
    copia.headers.set("cache-control", valor);
    return copia;
  }
});

const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

export const startInstance = createStart(() => ({
  requestMiddleware: [cacheMiddleware, redirectsMiddleware, trailingSlashMiddleware, errorMiddleware, csrfMiddleware],
  functionMiddleware: [attachSupabaseAuth],
}));
